import { ref } from "vue";
import type { FirestoreError } from "firebase/firestore";
import type {
  DeltaSyncSnapshot,
  FullSyncSnapshot,
} from "@/helpers/firestore/DeltaSyncService";
import {
  getCachedSyncState,
  setCachedDocuments,
} from "@/helpers/cache/document-cache";
import {
  applyDocumentChanges,
  type SyncableDocument,
} from "@/helpers/sync/apply-document-changes";
import { nextCursor } from "@/helpers/sync/sync-cursor";
import { needsFullResync } from "@/helpers/sync/needs-full-resync";

type ListenerCallbacks<S> = {
  onSnapshot: (snapshot: S) => void;
  onError: (error: FirestoreError) => void;
};

export type DeltaCollectionSource<T> = {
  /** IndexedDB collection name, e.g. "quotes". */
  cacheName: string;
  subscribeToAll: (
    groupId: string,
    callbacks: ListenerCallbacks<FullSyncSnapshot<T>>,
  ) => () => void;
  subscribeToChanges: (
    groupId: string,
    cursor: number,
    callbacks: ListenerCallbacks<DeltaSyncSnapshot<T>>,
  ) => () => void;
  countFromServer: (groupId: string) => Promise<number>;
  onError: (error: FirestoreError) => void;
};

/**
 * Keeps a per-group, timestamp-ordered document list in sync with Firestore
 * while reading as few documents as possible:
 *
 * 1. Show the IndexedDB copy instantly.
 * 2. No cursor yet → full sync once, then remember the newest `updated_at`.
 * 3. Listen only to documents with `updated_at` newer than that cursor.
 * 4. Deletes (and documents without `updated_at`) are invisible to step 3,
 *    so after every server snapshot compare an exact server count with the
 *    local count and fully resync when they differ.
 * 5. Writes from this device are invisible to step 3 until the server
 *    confirms them, so they are applied locally (see `writeLocally`).
 *
 * Only one group is listened to at a time.
 */
export function useDeltaCollection<T extends SyncableDocument>(
  source: DeltaCollectionSource<T>,
) {
  const documentsByGroup = ref<Record<string, T[]>>({});
  const loadingByGroup = ref<Record<string, boolean>>({});
  const isStaleByGroup = ref<Record<string, boolean>>({});

  /* Sync bookkeeping (non-reactive) */
  const cursorByGroup = new Map<string, number | null>();
  let activeUnsubscribe: (() => void) | null = null;
  let subscribedGroupId: string | null = null;
  // Bumped on every (un)subscribe so late async results from an old
  // subscription (IndexedDB read, count, listener) are ignored.
  let generation = 0;
  // Generation whose count request is in flight, plus whether another
  // snapshot arrived meanwhile and needs its own count afterwards.
  let countingGeneration: number | null = null;
  let recountRequested = false;
  // Full sync in progress: counts compared against a half-replaced list
  // would only restart it.
  let fullSyncRunning = false;
  // The local:server mismatch the last resync was started for. Seeing it
  // again means a full query cannot fix it (e.g. a document the
  // `orderBy("timestamp")` query skips), so we stop instead of looping.
  let lastResyncMismatch: string | null = null;
  // Unconfirmed writes from this device per group. They are in the local list
  // but not yet on the server, so counts are skipped until they settle.
  const pendingWritesByGroup = new Map<string, number>();
  let countDeferred = false;

  function documentsOf(groupId: string): T[] {
    return documentsByGroup.value[groupId] ?? [];
  }

  function persist(groupId: string): void {
    setCachedDocuments(
      source.cacheName,
      groupId,
      documentsOf(groupId),
      cursorByGroup.get(groupId) ?? null,
    ).catch((error) => {
      console.error(`Error saving ${source.cacheName} to IndexedDB:`, error);
    });
  }

  function stopListener(): void {
    activeUnsubscribe?.();
    activeUnsubscribe = null;
  }

  function handleError(error: FirestoreError, groupId: string): void {
    source.onError(error);
    loadingByGroup.value[groupId] = false;
  }

  function startFullSync(groupId: string, gen: number): void {
    stopListener();
    fullSyncRunning = true;
    activeUnsubscribe = source.subscribeToAll(groupId, {
      onSnapshot: ({ documents, latestUpdatedAt, metadata }) => {
        if (gen !== generation) return;
        loadingByGroup.value[groupId] = false;

        if (metadata.fromCache) {
          // Firestore's own cache: only useful when we have nothing to show.
          if (documentsOf(groupId).length === 0 && documents.length > 0) {
            documentsByGroup.value[groupId] = documents;
            isStaleByGroup.value[groupId] = true;
          }
          return;
        }

        // Authoritative full list: replaces whatever we had (drops deletes).
        documentsByGroup.value[groupId] = documents;
        cursorByGroup.set(groupId, nextCursor(null, latestUpdatedAt));
        isStaleByGroup.value[groupId] = false;
        persist(groupId);

        fullSyncRunning = false;
        startDeltaSync(groupId, gen);
      },
      onError: (error) => {
        if (gen === generation) handleError(error, groupId);
      },
    });
  }

  function startDeltaSync(groupId: string, gen: number): void {
    stopListener();

    activeUnsubscribe = source.subscribeToChanges(
      groupId,
      cursorByGroup.get(groupId) ?? 0,
      {
        onSnapshot: ({ changed, latestUpdatedAt, metadata }) => {
          if (gen !== generation) return;
          loadingByGroup.value[groupId] = false;
          documentsByGroup.value[groupId] = applyDocumentChanges(
            documentsOf(groupId),
            changed,
          );

          if (metadata.fromCache) {
            if (isStaleByGroup.value[groupId] !== false) {
              isStaleByGroup.value[groupId] = true;
            }
            return;
          }

          isStaleByGroup.value[groupId] = false;
          cursorByGroup.set(
            groupId,
            nextCursor(cursorByGroup.get(groupId) ?? null, latestUpdatedAt),
          );
          persist(groupId);

          // Unconfirmed local writes skew the local count, so only compare
          // once Firestore has confirmed them.
          if (!metadata.hasPendingWrites) {
            void verifyCount(groupId, gen);
          }
        },
        onError: (error) => {
          if (gen === generation) handleError(error, groupId);
        },
      },
    );
  }

  function hasPendingWrites(groupId: string): boolean {
    return (pendingWritesByGroup.get(groupId) ?? 0) > 0;
  }

  async function verifyCount(groupId: string, gen: number): Promise<void> {
    if (hasPendingWrites(groupId)) {
      countDeferred = true;
      return;
    }
    if (countingGeneration === gen) {
      recountRequested = true;
      return;
    }
    countingGeneration = gen;
    recountRequested = false;

    try {
      const serverCount = await source.countFromServer(groupId);
      // A snapshot arriving meanwhile may have changed the local list, so this
      // result is outdated: skip it and let the recount in `finally` compare.
      if (gen !== generation || fullSyncRunning || recountRequested) return;
      if (hasPendingWrites(groupId)) {
        countDeferred = true;
        return;
      }

      const localCount = documentsOf(groupId).length;
      if (!needsFullResync(localCount, serverCount)) {
        lastResyncMismatch = null;
        return;
      }

      const mismatch = `${localCount}:${serverCount}`;
      if (mismatch === lastResyncMismatch) {
        console.warn(
          `${source.cacheName} still differs from the server count after a full resync (${mismatch})`,
        );
        return;
      }
      lastResyncMismatch = mismatch;
      recountRequested = false;
      startFullSync(groupId, gen);
    } catch (error) {
      // Non-fatal: the delta listener keeps running; the next snapshot retries.
      console.error(`Error counting ${source.cacheName}:`, error);
    } finally {
      if (countingGeneration === gen) {
        countingGeneration = null;
        if (recountRequested && gen === generation) {
          void verifyCount(groupId, gen);
        }
      }
    }
  }

  async function resume(groupId: string, gen: number): Promise<void> {
    if (!cursorByGroup.has(groupId)) {
      const cached = await getCachedSyncState<T>(
        source.cacheName,
        groupId,
      ).catch((error) => {
        console.error(
          `Error reading cached ${source.cacheName} from IndexedDB:`,
          error,
        );
        return null;
      });
      if (gen !== generation) return;

      // A listener may have delivered data while IndexedDB was being read.
      if (cached && documentsOf(groupId).length === 0) {
        documentsByGroup.value[groupId] = cached.documents;
        if (cached.documents.length > 0) {
          loadingByGroup.value[groupId] = false;
          isStaleByGroup.value[groupId] = true;
        }
      }
      cursorByGroup.set(groupId, cached?.cursor ?? null);
    }

    if (cursorByGroup.get(groupId) == null) {
      startFullSync(groupId, gen);
    } else {
      startDeltaSync(groupId, gen);
    }
  }

  /**
   * Start syncing a group (stops the previous group's listener).
   * @returns false when this group was already being synced
   */
  function subscribe(groupId: string): boolean {
    if (subscribedGroupId === groupId) return false;
    unsubscribe();
    subscribedGroupId = groupId;
    const gen = generation;

    if (documentsOf(groupId).length === 0) {
      loadingByGroup.value[groupId] = true;
    }

    void resume(groupId, gen);
    return true;
  }

  function unsubscribe(): void {
    stopListener();
    subscribedGroupId = null;
    fullSyncRunning = false;
    lastResyncMismatch = null;
    countDeferred = false;
    generation++;
  }

  /**
   * Remove a document this device deleted, so the next count check does not
   * mistake it for a remote delete and trigger a full resync.
   */
  function removeLocally(groupId: string, documentId: string): void {
    documentsByGroup.value[groupId] = applyDocumentChanges(
      documentsOf(groupId),
      [],
      [documentId],
    );
    persist(groupId);
  }

  /**
   * Show a write from this device immediately, then run it.
   *
   * Until the server confirms the write, its `updated_at` is a pending
   * `serverTimestamp()`, which never matches the delta query in the local
   * cache: without this the change would only appear after the round trip,
   * and never while offline. Rolls the document back if the write fails.
   * @param document - The document as it will look after the write
   */
  async function writeLocally(
    groupId: string,
    document: T,
    write: () => Promise<void>,
  ): Promise<void> {
    const previous = documentsOf(groupId).find(
      (doc) => doc._id === document._id,
    );
    documentsByGroup.value[groupId] = applyDocumentChanges(
      documentsOf(groupId),
      [document],
    );
    pendingWritesByGroup.set(
      groupId,
      (pendingWritesByGroup.get(groupId) ?? 0) + 1,
    );

    try {
      await write();
    } catch (error) {
      documentsByGroup.value[groupId] = previous
        ? applyDocumentChanges(documentsOf(groupId), [previous])
        : applyDocumentChanges(documentsOf(groupId), [], [document._id]);
      throw error;
    } finally {
      const remaining = (pendingWritesByGroup.get(groupId) ?? 1) - 1;
      if (remaining > 0) {
        pendingWritesByGroup.set(groupId, remaining);
      } else {
        pendingWritesByGroup.delete(groupId);
        // Run the count that was skipped while the write was pending.
        if (countDeferred && subscribedGroupId === groupId) {
          countDeferred = false;
          void verifyCount(groupId, generation);
        }
      }
    }
  }

  function reset(): void {
    unsubscribe();
    cursorByGroup.clear();
    pendingWritesByGroup.clear();
    documentsByGroup.value = {};
    loadingByGroup.value = {};
    isStaleByGroup.value = {};
  }

  return {
    documentsByGroup,
    loadingByGroup,
    isStaleByGroup,
    subscribe,
    unsubscribe,
    writeLocally,
    removeLocally,
    reset,
  };
}
